import { useState, type ReactNode } from 'react';

import type { AccessibleBranch, Session } from '@core/session/types';
import { Sidebar } from './Sidebar';
import { GlobalFind } from './GlobalFind';
import { Topbar } from './Topbar';
import { useLayoutState } from './layoutState';
import { usePreline } from './preline';

/**
 * The application shell: the template's `main-wrapper` / `page-wrapper` frame, with our sidebar,
 * our topbar and the active screen inside it.
 *
 * The structure is the template's and the geometry lives entirely in its stylesheet — the floating
 * sidebar inset from three edges, the content column offset by `--sidenav-width`, the collapse to
 * a rail, the mobile drawer. None of it is expressed here, which is the point: this file composes,
 * it does not style. That is the same rule the old `theme.ts` header carried, pointed at a
 * different file.
 *
 * What this shell deliberately does not do is decide what belongs in the menu. The manifest is
 * filtered against the session inside `Sidebar`, so navigation stays a projection of authorization
 * rather than a second copy of it.
 */
export function AppShell({
  session,
  onBranchChange,
  onSignOut,
  onChangePassword,
  onSecurity,
  activeKey,
  onNavigate,
  children,
}: {
  session: Session;
  onBranchChange: (branch: AccessibleBranch) => void;
  onSignOut: () => void;

  /**
   * Opens the change-password screen. It lives in the account menu rather than in the sidebar
   * because the sidebar is a projection of permissions, and this is the one action every user
   * holds regardless of what they may do.
   */
  onChangePassword: () => void;

  /**
   * Opens the two-factor screen. Alongside the password action and for the same reason: securing
   * your own sign-in is not something a permission should be able to take away.
   */
  onSecurity: () => void;
  activeKey: string;
  /** The third argument carries a code the global find resolved — see `ScreenContext` in the registry. */
  onNavigate: (key: string, view?: string, find?: string, record?: number) => void;
  children: ReactNode;
}) {
  const layout = useLayoutState();

  // Preline attaches its dropdown behaviour by walking the DOM, and everything here is rendered
  // after it has already walked once. See the note in `preline.ts` — without this the branch
  // switcher is markup that looks like a control.
  usePreline();

  /*
    The find panel is owned here rather than by the topbar that opens it.

    App overlays and the sidebar share `z-9999`, so DOM order decides which covers which — and the
    sidebar is rendered below. Every other overlay in the product comes from page content, which is
    later still; a panel mounted inside the topbar came earlier and lost, leaving the sidebar bright
    and clickable beside it.
  */
  const [finding, setFinding] = useState(false);

  return (
    <div className={`main-wrapper${layout.menuOpened ? ' slide-nav' : ''}`}>
      <Topbar
        session={session}
        layout={layout}
        onBranchChange={onBranchChange}
        onSignOut={onSignOut}
        onChangePassword={onChangePassword}
        onSecurity={onSecurity}
        onFind={() => setFinding(true)}
      />

      <Sidebar session={session} activeKey={activeKey} onNavigate={onNavigate} layout={layout} />

      <div className="page-wrapper">
        <main className="p-3 lg:px-0 lg:py-6">{children}</main>
      </div>

      {/*
        The scrim behind the mobile drawer. Rendered unconditionally because the template's CSS
        transitions its opacity from a class rather than mounting it — removing the element when
        the drawer is shut would make it appear with no transition and leave the closing one
        stranded mid-fade.
      */}
      <div
        className={`sidebar-overlay${layout.menuOpened ? ' opened' : ''}`}
        onClick={layout.closeMobileMenu}
      />

      {/* Last, so it covers the sidebar above it — see the note on `finding`. */}
      {finding && (
        <GlobalFind
          session={session}
          // The find lands on the screen that lists that kind of record, with the code already
          // searched — and on the record itself when it settled on one booking. No saved view — a
          // code is not a day's work.
          onOpen={(key, find, view, record) => onNavigate(key, view, find, record)}
          onClose={() => setFinding(false)}
        />
      )}
    </div>
  );
}
